import { gql } from "@apollo/client";

// ─── QUERIES ────────────────────────────────────────────────────────────────────

export const GET_INVENTORY_INGREDIENTS = gql`
  query GetInventoryIngredients(
    $category: String
    $search: String
    $skip: Int
    $take: Int
  ) {
    getInventoryIngredients(
      category: $category
      search: $search
      skip: $skip
      take: $take
    ) {
      id
      name
      name_en
      sku
      unit
      cost
      category
      low_stock_threshold
      reorder_point
      status
      total_stock
      locations {
        id
        branch_id
        location
        quantity
        branch {
          id
          name
        }
      }
      conversions {
        id
        from_unit
        to_unit
        factor
      }
    }
  }
`;

export const GET_INVENTORY_INGREDIENT = gql`
  query GetInventoryIngredient($id: ID!) {
    getInventoryIngredient(id: $id) {
      id
      name
      name_en
      sku
      unit
      cost
      category
      low_stock_threshold
      reorder_point
      status
      deleted
      created_at
      updated_at
      total_stock
      locations {
        id
        branch_id
        location
        quantity
        branch {
          id
          name
        }
      }
      conversions {
        id
        from_unit
        to_unit
        factor
      }
      movements(limit: 20) {
        id
        quantity_change
        movement_type
        reference_type
        reference_id
        location
        cost_per_unit
        notes
        created_at
        branch {
          name
        }
      }
    }
  }
`;

export const GET_INGREDIENT_STOCK = gql`
  query GetIngredientStock(
    $ingredient_id: ID!
    $branch_id: Int
    $location: StorageLocation
  ) {
    getIngredientStock(
      ingredient_id: $ingredient_id
      branch_id: $branch_id
      location: $location
    ) {
      id
      branch_id
      location
      quantity
      branch {
        id
        name
      }
    }
  }
`;

export const GET_LOW_STOCK_INGREDIENTS = gql`
  query GetLowStockIngredients($branch_id: Int, $threshold: Float) {
    getLowStockIngredients(branch_id: $branch_id, threshold: $threshold) {
      id
      name
      unit
      low_stock_threshold
      total_stock
      locations {
        branch_id
        location
        quantity
        branch {
          name
        }
      }
    }
  }
`;

export const GET_INVENTORY_MOVEMENTS = gql`
  query GetInventoryMovements(
    $ingredient_id: Int
    $branch_id: Int
    $movement_type: MovementType
    $startDate: String
    $endDate: String
    $skip: Int
    $take: Int
  ) {
    getInventoryMovements(
      ingredient_id: $ingredient_id
      branch_id: $branch_id
      movement_type: $movement_type
      startDate: $startDate
      endDate: $endDate
      skip: $skip
      take: $take
    ) {
      id
      quantity_change
      movement_type
      reference_type
      reference_id
      location
      cost_per_unit
      notes
      performed_by
      created_at
      ingredient {
        id
        name
        unit
      }
      branch {
        id
        name
      }
    }
  }
`;

export const GET_RECIPES = gql`
  query GetRecipes($skip: Int, $take: Int) {
    getRecipes(skip: $skip, take: $take) {
      id
      product_id
      size_id
      name
      yield_qty
      status
      food_cost
      product {
        id
        title
        price
        has_size
      }
      ingredients {
        id
        ingredient_id
        quantity
        unit
        is_optional
        waste_factor
        inventory_ingredient {
          id
          name
          cost
        }
      }
    }
  }
`;

export const GET_RECIPE = gql`
  query GetRecipe($product_id: Int!, $size_id: Int) {
    getRecipe(product_id: $product_id, size_id: $size_id) {
      id
      product_id
      size_id
      name
      yield_qty
      notes
      status
      food_cost
      product {
        id
        title
        price
        has_size
        sizes {
          id
          name
          price
        }
      }
      ingredients {
        id
        ingredient_id
        quantity
        unit
        is_optional
        optional_ingredient_id
        waste_factor
        notes
        inventory_ingredient {
          id
          name
          unit
          cost
        }
      }
    }
  }
`;

export const GET_UNLINKED_PRODUCTS = gql`
  query GetUnlinkedProducts {
    getUnlinkedProducts {
      id
      title
      price
      has_size
      sizes {
        id
        name
        price
      }
    }
  }
`;

export const GET_PRODUCTS = gql`
  query GetProducts($take: Int, $skip: Int) {
    getProducts(take: $take, skip: $skip) {
      id
      title
      price
      has_size
      sizes {
        id
        name
        price
      }
    }
  }
`;

export const GET_PRODUCT_COST = gql`
  query GetProductCost($product_id: Int!, $size_id: Int) {
    getProductCost(product_id: $product_id, size_id: $size_id) {
      product_id
      recipe_id
      food_cost
      price
      margin
      margin_pct
      product {
        id
        title
        price
        has_size
      }
    }
  }
`;

// ─── MUTATIONS ──────────────────────────────────────────────────────────────────

export const CREATE_INVENTORY_INGREDIENT = gql`
  mutation CreateInventoryIngredient(
    $name: String!
    $name_en: String
    $sku: String
    $unit: InventoryUnit!
    $cost: Float
    $category: String
    $low_stock_threshold: Float
    $reorder_point: Float
  ) {
    createInventoryIngredient(
      name: $name
      name_en: $name_en
      sku: $sku
      unit: $unit
      cost: $cost
      category: $category
      low_stock_threshold: $low_stock_threshold
      reorder_point: $reorder_point
    ) {
      id
      name
    }
  }
`;

export const UPDATE_INVENTORY_INGREDIENT = gql`
  mutation UpdateInventoryIngredient(
    $id: ID!
    $name: String
    $name_en: String
    $sku: String
    $unit: InventoryUnit
    $cost: Float
    $category: String
    $low_stock_threshold: Float
    $reorder_point: Float
  ) {
    updateInventoryIngredient(
      id: $id
      name: $name
      name_en: $name_en
      sku: $sku
      unit: $unit
      cost: $cost
      category: $category
      low_stock_threshold: $low_stock_threshold
      reorder_point: $reorder_point
    ) {
      id
      name
    }
  }
`;

export const DELETE_INVENTORY_INGREDIENT = gql`
  mutation DeleteInventoryIngredient($id: ID!) {
    deleteInventoryIngredient(id: $id)
  }
`;

export const ADJUST_INVENTORY_STOCK = gql`
  mutation AdjustInventoryStock(
    $ingredient_id: ID!
    $branch_id: Int!
    $location: StorageLocation!
    $new_quantity: Float!
    $notes: String
  ) {
    adjustInventoryStock(
      ingredient_id: $ingredient_id
      branch_id: $branch_id
      location: $location
      new_quantity: $new_quantity
      notes: $notes
    ) {
      id
      quantity
    }
  }
`;

export const ADD_INVENTORY_MOVEMENT = gql`
  mutation AddInventoryMovement(
    $ingredient_id: ID!
    $branch_id: Int!
    $quantity_change: Float!
    $movement_type: MovementType!
    $reference_type: MovementReferenceType!
    $reference_id: Int
    $location: StorageLocation
    $cost_per_unit: Float
    $notes: String
  ) {
    addInventoryMovement(
      ingredient_id: $ingredient_id
      branch_id: $branch_id
      quantity_change: $quantity_change
      movement_type: $movement_type
      reference_type: $reference_type
      reference_id: $reference_id
      location: $location
      cost_per_unit: $cost_per_unit
      notes: $notes
    ) {
      id
    }
  }
`;

export const CREATE_UNIT_CONVERSION = gql`
  mutation CreateUnitConversion(
    $ingredient_id: ID!
    $from_unit: InventoryUnit!
    $to_unit: InventoryUnit!
    $factor: Float!
  ) {
    createUnitConversion(
      ingredient_id: $ingredient_id
      from_unit: $from_unit
      to_unit: $to_unit
      factor: $factor
    ) {
      id
      from_unit
      to_unit
      factor
    }
  }
`;

export const DELETE_UNIT_CONVERSION = gql`
  mutation DeleteUnitConversion($id: ID!) {
    deleteUnitConversion(id: $id)
  }
`;

export const CREATE_RECIPE = gql`
  mutation CreateRecipe(
    $product_id: Int!
    $size_id: Int
    $name: String
    $yield_qty: Float
    $notes: String
    $ingredients: [RecipeIngredientInput!]!
  ) {
    createRecipe(
      product_id: $product_id
      size_id: $size_id
      name: $name
      yield_qty: $yield_qty
      notes: $notes
      ingredients: $ingredients
    ) {
      id
      name
      food_cost
    }
  }
`;

export const UPDATE_RECIPE = gql`
  mutation UpdateRecipe(
    $id: ID!
    $name: String
    $yield_qty: Float
    $notes: String
    $ingredients: [RecipeIngredientInput!]
  ) {
    updateRecipe(
      id: $id
      name: $name
      yield_qty: $yield_qty
      notes: $notes
      ingredients: $ingredients
    ) {
      id
      name
      food_cost
    }
  }
`;

export const DELETE_RECIPE = gql`
  mutation DeleteRecipe($id: ID!) {
    deleteRecipe(id: $id)
  }
`;

// ─── PHASE 4: SUPPLIERS & PURCHASES ──────────────────────────────────────────

export const GET_SUPPLIERS = gql`
  query GetSuppliers($search: String, $skip: Int, $take: Int) {
    getSuppliers(search: $search, skip: $skip, take: $take) {
      id
      name
      contact_name
      phone
      email
      status
      ingredients {
        id
        ingredient_id
        ingredient {
          id
          name
        }
      }
    }
  }
`;

export const GET_SUPPLIER = gql`
  query GetSupplier($id: ID!) {
    getSupplier(id: $id) {
      id
      name
      contact_name
      phone
      email
      address
      notes
      status
      ingredients {
        id
        supplier_id
        ingredient_id
        unit_price
        unit
        min_order_qty
        lead_time_days
        sku
        is_preferred
        ingredient {
          id
          name
          unit
          cost
        }
      }
    }
  }
`;

export const GET_PURCHASE_ORDERS = gql`
  query GetPurchaseOrders(
    $supplier_id: Int
    $status: PurchaseOrderStatus
    $branch_id: Int
    $skip: Int
    $take: Int
  ) {
    getPurchaseOrders(
      supplier_id: $supplier_id
      status: $status
      branch_id: $branch_id
      skip: $skip
      take: $take
    ) {
      id
      supplier_id
      branch_id
      status
      order_date
      expected_date
      received_date
      subtotal
      tax
      total
      notes
      created_at
      supplier {
        id
        name
      }
      branch {
        id
        name
      }
      items {
        id
        ingredient_id
        quantity_ordered
        quantity_received
        quantity_damaged
        unit
        unit_price
        total
        ingredient {
          id
          name
        }
      }
    }
  }
`;

export const GET_PURCHASE_ORDER = gql`
  query GetPurchaseOrder($id: ID!) {
    getPurchaseOrder(id: $id) {
      id
      supplier_id
      branch_id
      status
      order_date
      expected_date
      received_date
      subtotal
      tax
      total
      notes
      created_by
      created_at
      updated_at
      supplier {
        id
        name
        contact_name
        phone
        email
      }
      branch {
        id
        name
      }
      items {
        id
        ingredient_id
        quantity_ordered
        quantity_received
        quantity_damaged
        unit
        unit_price
        total
        notes
        ingredient {
          id
          name
          unit
        }
      }
    }
  }
`;

export const GET_SUGGESTED_PURCHASE = gql`
  query GetSuggestedPurchase($branch_id: Int) {
    getSuggestedPurchase(branch_id: $branch_id) {
      ingredient {
        id
        name
        unit
      }
      current_stock
      reorder_point
      suggested_qty
      preferred_supplier {
        id
        name
      }
      unit_price
      lead_time_days
    }
  }
`;

export const GET_BRANCHES = gql`
  query GetBranches {
    getBranches {
      id
      name
    }
  }
`;

export const CREATE_SUPPLIER = gql`
  mutation CreateSupplier(
    $name: String!
    $contact_name: String
    $phone: String
    $email: String
    $address: String
    $notes: String
  ) {
    createSupplier(
      name: $name
      contact_name: $contact_name
      phone: $phone
      email: $email
      address: $address
      notes: $notes
    ) {
      id
      name
    }
  }
`;

export const UPDATE_SUPPLIER = gql`
  mutation UpdateSupplier(
    $id: ID!
    $name: String
    $contact_name: String
    $phone: String
    $email: String
    $address: String
    $notes: String
    $status: Boolean
  ) {
    updateSupplier(
      id: $id
      name: $name
      contact_name: $contact_name
      phone: $phone
      email: $email
      address: $address
      notes: $notes
      status: $status
    ) {
      id
      name
    }
  }
`;

export const DELETE_SUPPLIER = gql`
  mutation DeleteSupplier($id: ID!) {
    deleteSupplier(id: $id)
  }
`;

export const ADD_SUPPLIER_INGREDIENT = gql`
  mutation AddSupplierIngredient(
    $supplier_id: Int!
    $ingredient_id: Int!
    $unit_price: Float!
    $unit: InventoryUnit!
    $min_order_qty: Float
    $lead_time_days: Int
    $sku: String
    $is_preferred: Boolean
  ) {
    addSupplierIngredient(
      supplier_id: $supplier_id
      ingredient_id: $ingredient_id
      unit_price: $unit_price
      unit: $unit
      min_order_qty: $min_order_qty
      lead_time_days: $lead_time_days
      sku: $sku
      is_preferred: $is_preferred
    ) {
      id
    }
  }
`;

export const UPDATE_SUPPLIER_INGREDIENT = gql`
  mutation UpdateSupplierIngredient(
    $id: ID!
    $unit_price: Float
    $unit: InventoryUnit
    $min_order_qty: Float
    $lead_time_days: Int
    $sku: String
    $is_preferred: Boolean
  ) {
    updateSupplierIngredient(
      id: $id
      unit_price: $unit_price
      unit: $unit
      min_order_qty: $min_order_qty
      lead_time_days: $lead_time_days
      sku: $sku
      is_preferred: $is_preferred
    ) {
      id
    }
  }
`;

export const DELETE_SUPPLIER_INGREDIENT = gql`
  mutation DeleteSupplierIngredient($id: ID!) {
    deleteSupplierIngredient(id: $id)
  }
`;

export const CREATE_PURCHASE_ORDER = gql`
  mutation CreatePurchaseOrder(
    $supplier_id: Int!
    $branch_id: Int!
    $expected_date: String
    $notes: String
    $items: [PurchaseOrderItemInput!]!
  ) {
    createPurchaseOrder(
      supplier_id: $supplier_id
      branch_id: $branch_id
      expected_date: $expected_date
      notes: $notes
      items: $items
    ) {
      id
      status
      total
    }
  }
`;

export const UPDATE_PURCHASE_ORDER = gql`
  mutation UpdatePurchaseOrder(
    $id: ID!
    $supplier_id: Int
    $branch_id: Int
    $expected_date: String
    $notes: String
    $status: PurchaseOrderStatus
  ) {
    updatePurchaseOrder(
      id: $id
      supplier_id: $supplier_id
      branch_id: $branch_id
      expected_date: $expected_date
      notes: $notes
      status: $status
    ) {
      id
      status
    }
  }
`;

export const UPDATE_PURCHASE_ORDER_ITEMS = gql`
  mutation UpdatePurchaseOrderItems(
    $id: ID!
    $items: [PurchaseOrderItemInput!]!
  ) {
    updatePurchaseOrderItems(id: $id, items: $items) {
      id
      subtotal
      tax
      total
    }
  }
`;

export const RECEIVE_PURCHASE_ORDER = gql`
  mutation ReceivePurchaseOrder(
    $id: ID!
    $items: [ReceiveItemInput!]!
  ) {
    receivePurchaseOrder(id: $id, items: $items) {
      id
      status
      received_date
      items {
        id
        quantity_received
        quantity_damaged
      }
    }
  }
`;

export const CANCEL_PURCHASE_ORDER = gql`
  mutation CancelPurchaseOrder($id: ID!) {
    cancelPurchaseOrder(id: $id) {
      id
      status
    }
  }
`;

// ─── PHASE 5: STOCK COUNTS ─────────────────────────────────────────────────

export const GET_STOCK_COUNTS = gql`
  query GetStockCounts(
    $branch_id: Int
    $status: CountStatus
    $skip: Int
    $take: Int
  ) {
    getStockCounts(
      branch_id: $branch_id
      status: $status
      skip: $skip
      take: $take
    ) {
      id
      branch_id
      count_type
      status
      started_by
      started_at
      completed_at
      notes
      total_items
      total_variance_cost
      branch {
        id
        name
      }
    }
  }
`;

export const GET_STOCK_COUNT = gql`
  query GetStockCount($id: ID!) {
    getStockCount(id: $id) {
      id
      branch_id
      count_type
      status
      started_by
      completed_by
      started_at
      completed_at
      notes
      total_items
      total_variance_cost
      branch {
        id
        name
      }
      items {
        id
        stock_count_id
        ingredient_id
        location
        expected_qty
        counted_qty
        variance
        variance_reason
        variance_notes
        unit
        cost_at_count
        variance_cost
        ingredient {
          id
          name
          unit
          category
        }
      }
    }
  }
`;

export const GET_VARIANCE_REPORT = gql`
  query GetVarianceReport(
    $branch_id: Int
    $startDate: String
    $endDate: String
  ) {
    getVarianceReport(
      branch_id: $branch_id
      startDate: $startDate
      endDate: $endDate
    ) {
      total_counts
      total_items
      items_with_variance
      total_variance_cost
      by_reason {
        reason
        count
        total_variance_cost
      }
      by_ingredient {
        ingredient {
          id
          name
          unit
        }
        total_variance
        total_variance_cost
        count
      }
    }
  }
`;

export const START_STOCK_COUNT = gql`
  mutation StartStockCount(
    $branch_id: Int!
    $count_type: CountType!
    $ingredient_ids: [Int]
    $category: String
  ) {
    startStockCount(
      branch_id: $branch_id
      count_type: $count_type
      ingredient_ids: $ingredient_ids
      category: $category
    ) {
      id
      status
      count_type
      total_items
      branch {
        id
        name
      }
      items {
        id
        ingredient_id
        location
        expected_qty
        unit
        cost_at_count
        ingredient {
          id
          name
          unit
          category
        }
      }
    }
  }
`;

export const SUBMIT_STOCK_COUNT = gql`
  mutation SubmitStockCount(
    $count_id: Int!
    $items: [StockCountItemInput!]!
  ) {
    submitStockCount(count_id: $count_id, items: $items) {
      id
      status
      completed_at
      total_items
      total_variance_cost
      items {
        id
        expected_qty
        counted_qty
        variance
        variance_reason
        variance_cost
        ingredient {
          id
          name
        }
      }
    }
  }
`;

export const CANCEL_STOCK_COUNT = gql`
  mutation CancelStockCount($id: ID!) {
    cancelStockCount(id: $id) {
      id
      status
    }
  }
`;

// ─── PHASE 6: WASTE & SPOILAGE ─────────────────────────────────────────────

export const GET_WASTE_REPORT = gql`
  query GetWasteReport(
    $branch_id: Int
    $startDate: String!
    $endDate: String!
  ) {
    getWasteReport(
      branch_id: $branch_id
      startDate: $startDate
      endDate: $endDate
    ) {
      total_waste_cost
      total_entries
      by_ingredient {
        ingredient {
          id
          name
          unit
          cost
        }
        total_quantity
        total_cost
        count
      }
      by_reason {
        reason
        count
        total_cost
      }
    }
  }
`;

export const REPORT_WASTE = gql`
  mutation ReportWaste(
    $ingredient_id: ID!
    $branch_id: Int!
    $quantity: Float!
    $location: StorageLocation
    $reason: WasteReason!
    $notes: String
  ) {
    reportWaste(
      ingredient_id: $ingredient_id
      branch_id: $branch_id
      quantity: $quantity
      location: $location
      reason: $reason
      notes: $notes
    ) {
      id
      quantity_change
      movement_type
      notes
      created_at
    }
  }
`;

// ─── PHASE 7: PRODUCTION ──────────────────────────────────────────────────

export const GET_PRODUCTION_RECIPES = gql`
  query GetProductionRecipes($search: String, $skip: Int, $take: Int) {
    getProductionRecipes(search: $search, skip: $skip, take: $take) {
      id
      name
      output_quantity
      output_unit
      status
      output_ingredient {
        id
        name
        unit
      }
      inputs {
        id
        ingredient_id
        quantity
        unit
        ingredient {
          id
          name
          unit
          cost
        }
      }
    }
  }
`;

export const GET_PRODUCTION_RECIPE = gql`
  query GetProductionRecipe($id: ID!) {
    getProductionRecipe(id: $id) {
      id
      name
      output_ingredient_id
      output_quantity
      output_unit
      notes
      status
      created_at
      updated_at
      output_ingredient {
        id
        name
        unit
        cost
      }
      inputs {
        id
        ingredient_id
        quantity
        unit
        ingredient {
          id
          name
          unit
          cost
        }
      }
    }
  }
`;

export const GET_PRODUCTION_BATCHES = gql`
  query GetProductionBatches(
    $branch_id: Int
    $status: ProductionBatchStatus
    $recipe_id: Int
    $skip: Int
    $take: Int
  ) {
    getProductionBatches(
      branch_id: $branch_id
      status: $status
      recipe_id: $recipe_id
      skip: $skip
      take: $take
    ) {
      id
      branch_id
      production_recipe_id
      actual_output
      status
      performed_by
      started_at
      completed_at
      notes
      production_recipe {
        id
        name
        output_quantity
        output_unit
        output_ingredient {
          id
          name
          unit
        }
      }
      branch {
        id
        name
      }
    }
  }
`;

export const GET_PRODUCTION_BATCH = gql`
  query GetProductionBatch($id: ID!) {
    getProductionBatch(id: $id) {
      id
      branch_id
      production_recipe_id
      actual_output
      status
      performed_by
      started_at
      completed_at
      notes
      production_recipe {
        id
        name
        output_quantity
        output_unit
        output_ingredient {
          id
          name
          unit
          cost
        }
        inputs {
          id
          ingredient_id
          quantity
          unit
          ingredient {
            id
            name
            unit
            cost
          }
        }
      }
      branch {
        id
        name
      }
    }
  }
`;

export const CREATE_PRODUCTION_RECIPE = gql`
  mutation CreateProductionRecipe(
    $name: String!
    $output_ingredient_id: Int!
    $output_quantity: Float!
    $output_unit: InventoryUnit!
    $notes: String
    $inputs: [ProductionRecipeInputData!]!
  ) {
    createProductionRecipe(
      name: $name
      output_ingredient_id: $output_ingredient_id
      output_quantity: $output_quantity
      output_unit: $output_unit
      notes: $notes
      inputs: $inputs
    ) {
      id
      name
    }
  }
`;

export const UPDATE_PRODUCTION_RECIPE = gql`
  mutation UpdateProductionRecipe(
    $id: ID!
    $name: String
    $output_quantity: Float
    $output_unit: InventoryUnit
    $notes: String
    $status: Boolean
    $inputs: [ProductionRecipeInputData!]
  ) {
    updateProductionRecipe(
      id: $id
      name: $name
      output_quantity: $output_quantity
      output_unit: $output_unit
      notes: $notes
      status: $status
      inputs: $inputs
    ) {
      id
      name
    }
  }
`;

export const DELETE_PRODUCTION_RECIPE = gql`
  mutation DeleteProductionRecipe($id: ID!) {
    deleteProductionRecipe(id: $id)
  }
`;

export const START_PRODUCTION_BATCH = gql`
  mutation StartProductionBatch(
    $recipe_id: Int!
    $branch_id: Int!
    $notes: String
  ) {
    startProductionBatch(
      recipe_id: $recipe_id
      branch_id: $branch_id
      notes: $notes
    ) {
      id
      status
      started_at
      production_recipe {
        id
        name
      }
      branch {
        id
        name
      }
    }
  }
`;

export const COMPLETE_PRODUCTION_BATCH = gql`
  mutation CompleteProductionBatch(
    $id: ID!
    $actual_output: Float!
    $notes: String
  ) {
    completeProductionBatch(
      id: $id
      actual_output: $actual_output
      notes: $notes
    ) {
      id
      status
      actual_output
      completed_at
    }
  }
`;

export const CANCEL_PRODUCTION_BATCH = gql`
  mutation CancelProductionBatch($id: ID!) {
    cancelProductionBatch(id: $id) {
      id
      status
    }
  }
`;

// ─── PHASE 8: COSTING & MARGINS ─────────────────────────────────────────────

export const GET_MENU_PROFITABILITY = gql`
  query GetMenuProfitability($category: String) {
    getMenuProfitability(category: $category) {
      total_items
      items_with_recipe
      avg_margin_pct
      items {
        product_id
        product {
          id
          title
          price
        }
        price
        food_cost
        margin
        margin_pct
        has_recipe
      }
    }
  }
`;

export const GET_COST_DRIVERS = gql`
  query GetCostDrivers(
    $branch_id: Int
    $startDate: String!
    $endDate: String!
    $limit: Int
  ) {
    getCostDrivers(
      branch_id: $branch_id
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      total_cost
      startDate
      endDate
      items {
        ingredient {
          id
          name
          unit
          cost
        }
        total_quantity
        total_cost
        pct_of_total
      }
    }
  }
`;

export const GET_FOOD_COST_REPORT = gql`
  query GetFoodCostReport(
    $branch_id: Int
    $startDate: String!
    $endDate: String!
  ) {
    getFoodCostReport(
      branch_id: $branch_id
      startDate: $startDate
      endDate: $endDate
    ) {
      total_ingredient_cost
      total_revenue
      food_cost_pct
      startDate
      endDate
    }
  }
`;

export const GET_INVENTORY_VALUATION = gql`
  query GetInventoryValuation($branch_id: Int) {
    getInventoryValuation(branch_id: $branch_id) {
      total_value
      total_items
      items {
        ingredient {
          id
          name
          unit
          cost
          category
        }
        total_stock
        cost_per_unit
        total_value
      }
    }
  }
`;

export const GET_THEORETICAL_VS_ACTUAL = gql`
  query GetTheoreticalVsActualReport(
    $branch_id: Int
    $startDate: String!
    $endDate: String!
  ) {
    getTheoreticalVsActualReport(
      branch_id: $branch_id
      startDate: $startDate
      endDate: $endDate
    ) {
      startDate
      endDate
      items {
        ingredient {
          id
          name
          unit
          cost
        }
        theoretical_qty
        actual_qty
        variance
        variance_pct
        unit
      }
    }
  }
`;
